import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Inbox, FileText, Briefcase, X } from 'lucide-react';
import { adminApi } from './adminApi';
import { TierPill } from './AdminDashboard';

const useDebounce = (value, ms = 250) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return debounced;
};

const GlobalSearch = () => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState({ leads: [], articles: [], case_studies: [] });
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);
  const debounced = useDebounce(query, 200);

  // Cmd/Ctrl + K shortcut
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen(true);
        setTimeout(() => ref.current?.querySelector('input')?.focus(), 50);
      }
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Run search when debounced value changes
  useEffect(() => {
    if (!open) return;
    const q = debounced.trim();
    if (q.length < 2) {
      setResults({ leads: [], articles: [], case_studies: [] });
      return;
    }
    let cancelled = false;
    setLoading(true);
    adminApi.search(q)
      .then((data) => { if (!cancelled) setResults(data); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [debounced, open]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleNavigate = (path) => {
    setOpen(false);
    setQuery('');
    navigate(path);
  };

  const total = results.leads.length + results.articles.length + results.case_studies.length;

  return (
    <>
      <button
        onClick={() => { setOpen(true); setTimeout(() => ref.current?.querySelector('input')?.focus(), 50); }}
        data-testid="global-search-trigger"
        className="hidden md:flex items-center gap-2 px-3 py-2 rounded-full border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] text-white/40 text-xs font-['Inter'] transition w-64"
      >
        <Search className="w-3.5 h-3.5" />
        <span>Search leads, articles, cases…</span>
        <span className="ml-auto text-[10px] bg-white/5 px-1.5 py-0.5 rounded border border-white/10">⌘K</span>
      </button>

      {open && (
        <div className="fixed inset-0 z-[60] flex items-start justify-center pt-20 px-6 bg-black/60 backdrop-blur-sm" data-testid="global-search-modal">
          <div ref={ref} className="w-full max-w-2xl bg-[#15151A] border border-white/10 rounded-2xl shadow-2xl overflow-hidden">
            <div className="flex items-center gap-3 px-5 py-4 border-b border-white/5">
              <Search className="w-4 h-4 text-white/40 shrink-0" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search leads, articles, cases…"
                data-testid="global-search-input"
                className="flex-1 bg-transparent text-white text-sm font-['Inter'] placeholder-white/30 focus:outline-none"
              />
              {query && (
                <button onClick={() => setQuery('')} data-testid="global-search-clear" className="text-white/30 hover:text-white">
                  <X className="w-4 h-4" />
                </button>
              )}
              <kbd className="text-[10px] text-white/30 bg-white/5 px-1.5 py-0.5 rounded border border-white/10">ESC</kbd>
            </div>

            <div className="max-h-[60vh] overflow-y-auto">
              {query.trim().length < 2 ? (
                <p className="text-white/30 text-sm font-['Lato'] py-12 text-center" data-testid="global-search-hint">
                  Type at least 2 characters to search.
                </p>
              ) : loading ? (
                <p className="text-white/30 text-sm font-['Lato'] py-12 text-center">Searching…</p>
              ) : total === 0 ? (
                <p className="text-white/30 text-sm font-['Lato'] py-12 text-center" data-testid="global-search-empty">
                  No results for "{query}".
                </p>
              ) : (
                <>
                  {results.leads.length > 0 && (
                    <SearchSection title="Leads" icon={Inbox}>
                      {results.leads.map((l) => (
                        <button
                          key={l.id}
                          onClick={() => handleNavigate('/admin/contacts')}
                          data-testid={`search-lead-${l.id}`}
                          className="w-full text-left px-5 py-3 hover:bg-white/[0.03] flex items-center gap-3"
                        >
                          <div className="min-w-0 flex-1">
                            <p className="text-white text-sm font-['Inter'] flex items-center gap-2">
                              {l.firstName} {l.lastName}
                              {l.score_tier && l.score_tier !== 'cold' && (
                                <TierPill tier={l.score_tier} score={l.score} />
                              )}
                            </p>
                            <p className="text-white/40 text-xs font-['Lato'] truncate">
                              {l.company} · {l.email}
                            </p>
                          </div>
                          <span className="text-[10px] text-white/40 uppercase tracking-wider">{l.status}</span>
                        </button>
                      ))}
                    </SearchSection>
                  )}

                  {results.articles.length > 0 && (
                    <SearchSection title="Articles" icon={FileText}>
                      {results.articles.map((a) => (
                        <button
                          key={a.id}
                          onClick={() => handleNavigate(`/admin/articles/${a.slug}`)}
                          data-testid={`search-article-${a.slug}`}
                          className="w-full text-left px-5 py-3 hover:bg-white/[0.03] flex items-center gap-3"
                        >
                          {a.image && <img src={a.image} alt="" className="w-10 h-10 rounded object-cover shrink-0" />}
                          <div className="min-w-0 flex-1">
                            <p className="text-white text-sm font-['Inter'] truncate">{a.title}</p>
                            <p className="text-white/40 text-xs font-['Lato']">{a.category}</p>
                          </div>
                          {a.published === false && (
                            <span className="text-[10px] text-amber-300 bg-amber-500/15 px-2 py-0.5 rounded-full">Draft</span>
                          )}
                        </button>
                      ))}
                    </SearchSection>
                  )}

                  {results.case_studies.length > 0 && (
                    <SearchSection title="Case Studies" icon={Briefcase}>
                      {results.case_studies.map((c) => (
                        <button
                          key={c.id}
                          onClick={() => handleNavigate(`/admin/cases/${c.id}`)}
                          data-testid={`search-case-${c.id}`}
                          className="w-full text-left px-5 py-3 hover:bg-white/[0.03] flex items-center gap-3"
                        >
                          {c.heroImage && <img src={c.heroImage} alt="" className="w-10 h-10 rounded object-cover shrink-0" />}
                          <div className="min-w-0 flex-1">
                            <p className="text-white text-sm font-['Inter'] truncate">{c.title || c.client}</p>
                            <p className="text-white/40 text-xs font-['Lato']">{c.client} · {c.industry}</p>
                          </div>
                        </button>
                      ))}
                    </SearchSection>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const SearchSection = ({ title, icon: Icon, children }) => (
  <div className="border-b border-white/5 last:border-b-0">
    <div className="px-5 py-2 bg-white/[0.02] flex items-center gap-2 text-[10px] uppercase tracking-wider font-['Inter'] text-white/40">
      <Icon className="w-3 h-3" /> {title}
    </div>
    {children}
  </div>
);

export default GlobalSearch;
